import { ref } from "vue";
import { isNPIValid, isPhoneNumberValid, isZipCodeValid } from "@/utils/validators";
import { orderInfoService } from "@/services/orderInfoService";

export function useOrderInfoUpdates(orderInfo) {
  const supplementalDocs = ref([]);
  const formButtonClicked = ref(true);

  // Update functions
  function updateChecklistSelection(newChecklistSelection, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.checklist = orderInfo.value.checklist.filter((item) => item.id !== unselectedItem.id);
      });
    } else {
      // Only keep items that are in the new selection (currently visible/answered in UI)
      // This ensures we don't save questions that are no longer visible or not yet answered
      const newSelectionIds = new Set(newChecklistSelection.map((item) => item.id));

      // Update existing items that are in the new selection
      const updatedChecklist = orderInfo.value.checklist
        .filter((item) => newSelectionIds.has(item.id))
        .map((item) => {
          const newItem = newChecklistSelection.find((newItem) => newItem.id === item.id);
          return newItem || item;
        });

      // Add new items that aren't in the existing checklist
      newChecklistSelection.forEach((selectedItem) => {
        if (!updatedChecklist.find((item) => item.id === selectedItem.id)) {
          updatedChecklist.push(selectedItem);
        }
      });

      orderInfo.value.checklist = updatedChecklist;
    }
  }

  function updateBasicPlusChecklistSelection(newChecklistSelection, patientChartNotes, isDeselect = false) {
    if (isDeselect) {
      newChecklistSelection.forEach((unselectedItem) => {
        orderInfo.value.basicPlusChecklist = orderInfo.value.basicPlusChecklist.filter(
          (item) => item.id !== unselectedItem.id
        );
      });
    } else {
      let updatedChecklist = [];
      newChecklistSelection.forEach((selectedItem) => {
        updatedChecklist = orderInfo.value.basicPlusChecklist.map((item) => {
          if (item.id === selectedItem.id) {
            return selectedItem;
          }
          return item;
        });
        if (!updatedChecklist.find((item) => item.id === selectedItem.id)) {
          updatedChecklist.push(selectedItem);
        }
      });
      orderInfo.value.basicPlusChecklist = updatedChecklist;
    }
  }

  function updateSignature(signature) {
    orderInfo.value.signature = signature;
  }

  function updatePrimaryDiagnosis({ icd10Code, name }) {
    orderInfo.value.primaryDiagnosisIcd10Code = icd10Code;
    orderInfo.value.primaryDiagnosisName = name;
  }

  function updateOtherDiagnosis({ index, icd10Code, name }) {
    if (!icd10Code || !name) {
      orderInfo.value.secondaryDiagnosis.splice(index, 1);
      orderInfo.value.secondaryDiagnosis.push({
        name: null,
        icd10Code: null,
      });
    } else {
      const insertIndex = orderInfo.value.secondaryDiagnosis.findIndex((item) => !item.name);
      orderInfo.value.secondaryDiagnosis[insertIndex]["icd10Code"] = icd10Code;
      orderInfo.value.secondaryDiagnosis[insertIndex]["name"] = name;
    }
  }

  function addOtherDiagnosis() {
    orderInfo.value.secondaryDiagnosis.push({ icd10Code: null, name: null });
  }

  function updateOrderInfoField(field, value) {
    orderInfo.value[field] = value;
  }

  function addFreeFormSupplementalDocs(downloadLinkInfo) {
    if (!supplementalDocs.value[downloadLinkInfo.storeVarIndex]) {
      supplementalDocs.value[downloadLinkInfo.storeVarIndex] = { label: null, links: [] };
    }
    supplementalDocs.value[downloadLinkInfo.storeVarIndex].links.push(downloadLinkInfo.link);
    supplementalDocs.value[downloadLinkInfo.storeVarIndex].label = downloadLinkInfo.fileName;
  }

  function updateSupportingDocs(downloadLinkInfo) {
    if (downloadLinkInfo.label) {
      const foundItem = orderInfo.value[downloadLinkInfo.storeVar].find(
        (item) => item.label === downloadLinkInfo.label && item.links.includes(downloadLinkInfo.link)
      );
      if (foundItem) {
        if (downloadLinkInfo.isDelete) {
          // Remove the specific link
          foundItem.links = foundItem.links.filter((link) => link !== downloadLinkInfo.link);
          // Remove the entire document if no links remain
          if (foundItem.links.length === 0) {
            orderInfo.value[downloadLinkInfo.storeVar] = orderInfo.value[downloadLinkInfo.storeVar].filter(
              (item) => item.links.length > 0
            );
          }
        } else {
          foundItem.links.push(downloadLinkInfo.link);
        }
      }
    } else {
      if (downloadLinkInfo.isDelete) {
        // Remove link from array
        orderInfo.value[downloadLinkInfo.storeVar] = orderInfo.value[downloadLinkInfo.storeVar].filter(
          (link) => link !== downloadLinkInfo.link
        );
      } else {
        orderInfo.value[downloadLinkInfo.storeVar]
          ? orderInfo.value[downloadLinkInfo.storeVar].push(downloadLinkInfo.link)
          : (orderInfo.value[downloadLinkInfo.storeVar] = [downloadLinkInfo.link]);
      }
    }
  }

  function addExtraMedication() {
    orderInfo.value.extraMedications.push({
      medication: "",
      strength: "",
      expectedLengthOfTherapy: "",
      quantityPer30Days: "",
      packageSizeUnit: "",
      frequency: "",
      dailyDosage: "",
      nationalDrugCode: "",
      routeAdministration: "",
      treatmentStartDate: "",
    });
  }

  function removeExtraMedication(index) {
    orderInfo.value.extraMedications.splice(index, 1);
  }

  function removePrimaryMedication() {
    if (orderInfo.value.extraMedications.length > 0) {
      let newPrimaryMedication = orderInfo.value.extraMedications.splice(0, 1);
      orderInfo.value.medication = newPrimaryMedication[0].medication;
      orderInfo.value.strength = newPrimaryMedication[0].strength;
      orderInfo.value.expectedLengthOfTherapy = newPrimaryMedication[0].expectedLengthOfTherapy;
      orderInfo.value.quantityPer30Days = newPrimaryMedication[0].quantityPer30Days;
      orderInfo.value.packageSizeUnit = newPrimaryMedication[0].packageSizeUnit;
      orderInfo.value.routeAdministration = newPrimaryMedication[0].routeAdministration;
      orderInfo.value.dailyDosage = newPrimaryMedication[0].dailyDosage;
      orderInfo.value.nationalDrugCode = newPrimaryMedication[0].nationalDrugCode;
      orderInfo.value.treatmentStartDate = newPrimaryMedication[0].treatmentStartDate;
      orderInfo.value.frequency = newPrimaryMedication[0].frequency;
    } else {
      orderInfo.value.medication = "";
      orderInfo.value.strength = "";
      orderInfo.value.expectedLengthOfTherapy = "";
      orderInfo.value.quantityPer30Days = "";
      orderInfo.value.dailyDosage = "";
      orderInfo.value.packageSizeUnit = "";
      orderInfo.value.routeAdministration = "";
      orderInfo.value.nationalDrugCode = "";
      orderInfo.value.treatmentStartDate = "";
      orderInfo.value.frequency = "";
    }
  }

  function updateExtraMedicationField(index, field, newValue) {
    orderInfo.value.extraMedications[index][field] = newValue;
  }

  function mergeSupplementalDocsWithPatientChart() {
    if (!Array.isArray(supplementalDocs.value)) {
      supplementalDocs.value = [];
    }

    if (!Array.isArray(orderInfo.value.patientChart)) {
      orderInfo.value.patientChart = [];
    }

    supplementalDocs.value.forEach((doc) => {
      if (doc.links?.length > 0) {
        orderInfo.value.patientChart.push({
          label: doc.label,
          links: [...doc.links],
        });
      }
    });

    supplementalDocs.value = [];
  }

  // Validation functions
  function addressValid(field) {
    const value = orderInfo.value[field];
    return value !== null && value.trim() !== "" && value.length <= 35;
  }

  function validPatientInfo() {
    const requiredFields = [
      "patientFirstName",
      "patientLastName",
      "patientDob",
      "patientGender",
      "patientAddress1",
      "patientCity",
      "patientState",
      "patientZip",
      "patientMemberId",
      "benefitType",
    ];

    if (orderInfo.value.submissionMethod === "CoverMyMeds") {
      requiredFields.push("bin");
    }

    return (
      requiredFields.every((field) => {
        const value = orderInfo.value[field];
        return value !== null && value.trim() !== "";
      }) &&
      addressValid("patientAddress1") &&
      isZipCodeValid(orderInfo.value.patientZip)
    );
  }

  function validProviderInfo() {
    const requiredFields = [
      "providerFirstName",
      "providerLastName",
      "providerContactPhone",
      "providerContactFax",
      "providerAddress1",
      "providerCity",
      "providerState",
      "providerZip",
      "providerNpiNumber",
    ];

    return (
      requiredFields.every((field) => {
        const value = orderInfo.value[field];
        return value !== null && value.trim() !== "";
      }) &&
      isNPIValid(orderInfo.value.providerNpiNumber) &&
      addressValid("providerAddress1") &&
      isZipCodeValid(orderInfo.value.providerZip) &&
      isPhoneNumberValid(orderInfo.value.providerContactPhone) &&
      isPhoneNumberValid(orderInfo.value.providerContactFax)
    );
  }

  function validMedicationInfo() {
    const requiredFields = ["medication"];

    const numericalFields = ["expectedLengthOfTherapy", "quantityPer30Days"];

    let ndcValidation = [];
    if (orderInfo.value.submissionMethod === "CoverMyMeds") {
      requiredFields.push("packageSizeUnit");
      ndcValidation.push("nationalDrugCode");
    }

    return (
      requiredFields.every((field) => {
        const value = orderInfo.value[field];
        return value !== null && value.trim() !== "";
      }) &&
      numericalFields.every((field) => {
        const value = orderInfo.value[field];
        return value !== null && !isNaN(value) && value.trim() !== "";
      }) &&
      ndcValidation.every((field) => {
        const value = orderInfo.value[field];
        return value !== null && value.trim() !== "" && value.length === 11 && !isNaN(value);
      })
    );
  }

  function validDiagnosisInfo() {
    const requiredFields = ["primaryDiagnosisIcd10Code", "primaryDiagnosisName"];
    return requiredFields.every((field) => {
      const value = orderInfo.value[field];
      return value !== null && value.trim() !== "";
    });
  }

  function validSupportingDocs(required = true) {
    if (!required) return true;
    const requiredFields = ["patientChart"];
    return requiredFields.every((field) => {
      const value = orderInfo.value[field];
      return (
        value !== null &&
        Array.isArray(value) &&
        value.length > 0 &&
        value.some((doc) => doc.links && doc.links.length > 0)
      );
    });
  }

  // Save functions
  async function savePartialOrderInfo() {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    if (!orderInfo.value.orderKey) {
      const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
      orderInfo.value.orderKey = newOrder.orderKey;
    }

    // Filter checklist to only include items with valid answers (options with id and label)
    // This ensures we don't save questions that are not currently visible/answered
    if (orderInfo.value.checklist && Array.isArray(orderInfo.value.checklist)) {
      orderInfo.value.checklist = orderInfo.value.checklist.filter(
        (item) =>
          item.options &&
          Array.isArray(item.options) &&
          item.options.length > 0 &&
          item.options.some((opt) => opt.id && opt.label)
      );
    }

    await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
  }

  return {
    // State
    supplementalDocs,
    formButtonClicked,

    // Update functions
    updateChecklistSelection,
    updateBasicPlusChecklistSelection,
    updateSignature,
    updatePrimaryDiagnosis,
    updateOtherDiagnosis,
    addOtherDiagnosis,
    updateOrderInfoField,
    addFreeFormSupplementalDocs,
    updateSupportingDocs,
    addExtraMedication,
    removeExtraMedication,
    removePrimaryMedication,
    updateExtraMedicationField,

    // Validation functions
    addressValid,
    validPatientInfo,
    validProviderInfo,
    validMedicationInfo,
    validDiagnosisInfo,
    validSupportingDocs,

    // Utility functions
    mergeSupplementalDocsWithPatientChart,

    // Save functions
    savePartialOrderInfo,
  };
}
